import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { queryKeys } from '@/lib/api/query-keys';

import { orderKeys } from '../hooks';

import {
  createOrderReplacementRequest,
  getOrderReplacementRequest,
  listOrderReplacementRequests,
  listReplacementEligibleItems,
} from './api';
import type { RequestReplacementValues } from './types';

/**
 * The order's replacement requests + the backend's window verdict. Called by `OrderActionsMenu`,
 * which mounts with Order Details, and enabled only for delivered orders — so by the time the
 * operator opens the menu and clicks, routing is a synchronous read of cached data (ADR-0022).
 */
export function useOrderReplacementRequests(orderId: string, enabled: boolean) {
  return useQuery({
    queryKey: orderKeys.replacementRequests(orderId),
    queryFn: () => listOrderReplacementRequests(orderId),
    enabled: enabled && Boolean(orderId),
  });
}

/** One replacement request's detail, for the Ticket Preview drawer. Takes an explicit `enabled`
 *  like its sibling above rather than reading an empty-string sentinel, so a closed drawer that
 *  stays mounted never fetches. */
export function useOrderReplacementRequest(orderId: string, requestId: string, enabled: boolean) {
  return useQuery({
    queryKey: orderKeys.replacementRequest(orderId, requestId),
    queryFn: () => getOrderReplacementRequest(orderId, requestId),
    enabled: enabled && Boolean(orderId) && Boolean(requestId),
  });
}

/** Eligible line items + their eligible unit numbers, fetched when the intake drawer opens. */
export function useReplacementEligibleItems(orderId: string, enabled: boolean) {
  return useQuery({
    queryKey: orderKeys.replacementEligibleItems(orderId),
    queryFn: () => listReplacementEligibleItems(orderId),
    enabled: enabled && Boolean(orderId),
  });
}

/**
 * Raises a replacement request against one Unit. Invalidates the order's replacement-requests
 * listing so the rows underneath refresh (ADR-0022: "a successful POST invalidates it"; this
 * ticket's AC: "A successful submission refreshes the Order's replacement listing"). Also
 * invalidates the eligible items, since the unit just requested is no longer eligible.
 */
export function useCreateOrderReplacement(orderId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (values: RequestReplacementValues) =>
      createOrderReplacementRequest(orderId, values),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: orderKeys.replacementRequests(orderId) });
      void qc.invalidateQueries({ queryKey: orderKeys.replacementEligibleItems(orderId) });
      // The new request joins the Replacements queue and the Dashboard's open-request counts.
      void qc.invalidateQueries({ queryKey: queryKeys.replacements.all });
      void qc.invalidateQueries({ queryKey: queryKeys.dashboard.quickActions });
    },
  });
}
